import { Fragment, ReactNode } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

const getPlacementClasses = (placement: string = 'bottom') => {
  const map: Record<string, string> = {
    top: 'bottom-full left-1/2 -translate-x-1/2',
    'top-start': 'bottom-full left-0',
    'top-end': 'bottom-full right-0',
    bottom: 'top-full left-1/2 -translate-x-1/2',
    'bottom-start': 'top-full left-0',
    'bottom-end': 'top-full right-0',
    left: 'right-full top-1/2 -translate-y-1/2',
    'left-start': 'right-full top-0',
    'left-end': 'right-full bottom-0',
    right: 'left-full top-1/2 -translate-y-1/2',
    'right-start': 'left-full top-0',
    'right-end': 'left-full bottom-0',
  };
  return map[placement] || map.bottom;
};

type DropdownProps = {
  label?: ReactNode;
  children: ReactNode;
  arrowIcon?: boolean;
  inline?: boolean;
  className?: string;
  placement?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end'
    | 'right'
    | 'right-start'
    | 'right-end';
};

export const Dropdown = ({
  label = 'Options',
  children,
  arrowIcon = true,
  inline = false,
  className = '',
  placement = 'bottom',
}: DropdownProps) => {
  return (
    <Menu
      as="div"
      className={`relative ${inline ? 'inline-block' : 'block'} text-left ${className}`}
    >
      <Menu.Button className="flex item-center justify-center">
        {label}
        {arrowIcon && <ChevronDownIcon className="ml-2 -mr-1 h-5 w-5" aria-hidden="true" />}
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 "
        enterTo="opacity-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Menu.Items
          className={`absolute z-50 mt-2 w-56 origin-top-right bg-white shadow-lg ring-1 ring-black ring-opacity-5 ${getPlacementClasses(
            placement
          )}`}
        >
          <div className="py-1">{children}</div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

// ✅ Subcomponents defined *outside* and assigned below
Dropdown.Header = ({ children }: { children: ReactNode }) => (
  <div className="px-4 py-3 text-sm text-gray-900">{children}</div>
);

Dropdown.Item = ({
  icon: Icon,
  children,
  onClick,
  className,
}: {
  icon?: React.ElementType;
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) => (
  <Menu.Item>
    {({ active }) => (
      <button
        onClick={onClick}
        className={`${
          active ? 'bg-gray-100' : ''
        } flex w-full items-center px-4 py-2 text-sm bg-white text-defaultText hover:bg-gray-100 hover:bg-opacity-70 ${className}`}
      >
        {Icon && <Icon className="mr-2 h-5 w-5 text-gray-400" />}
        {children}
      </button>
    )}
  </Menu.Item>
);

Dropdown.Divider = () => <div className="my-1 border-t border-gray-200" />;
